import ITokenResponse from "js-pkce/dist/ITokenResponse";

import { getAuthClient } from "../../index";

type BroadcastPayload = {
  result?: ITokenResponse;
  error?: string;
  timestamp: number;
};

type Lock = { owner: string; timestamp: number };

const isMessageBroadcastPayload = (
  message: unknown,
): message is BroadcastPayload => {
  return (
    typeof message === "object" &&
    message !== null &&
    ("result" in message || "error" in message) &&
    "timestamp" in message
  );
};

class RefreshService {
  private localRefreshPromise: Promise<ITokenResponse> | null = null;
  private readonly LOCK_KEY = "auth_refresh_lock";
  private readonly RESULT_KEY = "auth_refresh_result";
  private readonly LOCK_TIMEOUT = 10000;
  private readonly TAB_ID = crypto.randomUUID();
  private readonly CHANNEL_NAME = "auth_refresh_channel";
  private readonly bc: BroadcastChannel | null;

  constructor() {
    this.bc =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel(this.CHANNEL_NAME)
        : null;
  }

  public refreshToken(refreshToken: string): Promise<ITokenResponse> {
    if (this.localRefreshPromise) return this.localRefreshPromise;

    this.localRefreshPromise = (async () => {
      const now = Date.now();
      const currentLock = this.getLock();

      if (!currentLock || this.isLockStale(currentLock)) {
        this.setLock({ owner: this.TAB_ID, timestamp: now });

        try {
          const result = await getAuthClient().refreshAccessToken(refreshToken);
          this.broadcast({ result, timestamp: Date.now() });

          return result;
        } catch (err: any) {
          const errorMsg = err?.message || "Token refresh failed";
          this.broadcast({ error: errorMsg, timestamp: Date.now() });
          throw new Error(errorMsg);
        } finally {
          if (this.getLock()?.owner === this.TAB_ID) {
            this.removeLock();
          }
          this.localRefreshPromise = null;
        }
      } else {
        return await this.waitForResultFromOtherTab();
      }
    })();

    return this.localRefreshPromise;
  }

  // ---------- Internal helpers ----------

  private getLock(): Lock | null {
    try {
      return JSON.parse(localStorage.getItem(this.LOCK_KEY) || "null");
    } catch {
      return null;
    }
  }

  private setLock(lock: Lock) {
    localStorage.setItem(this.LOCK_KEY, JSON.stringify(lock));
  }

  private removeLock() {
    localStorage.removeItem(this.LOCK_KEY);
  }

  private isLockStale(lock: Lock | null): boolean {
    return !lock || Date.now() - lock.timestamp > this.LOCK_TIMEOUT;
  }

  private broadcast(payload: BroadcastPayload) {
    if (this.bc) {
      this.bc.postMessage(payload);
    } else {
      localStorage.setItem(this.RESULT_KEY, JSON.stringify(payload));
    }
  }

  private waitForResultFromOtherTab(): Promise<ITokenResponse> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error("Timeout waiting for token refresh from another tab"));
      }, this.LOCK_TIMEOUT + 1000);

      const handleMessage = (data: BroadcastPayload) => {
        cleanup();
        this.clearResult();
        if (data?.result) resolve(data.result);
        else reject(new Error(data.error));
      };

      const handleStorage = (event: StorageEvent) => {
        if (event.key === this.RESULT_KEY && event.newValue) {
          const data = JSON.parse(event.newValue);
          if (isMessageBroadcastPayload(data)) {
            handleMessage(data);
          }
        }
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.localRefreshPromise = null;
        if (this.bc) {
          this.bc.removeEventListener("message", onBroadcast);
        } else {
          window.removeEventListener("storage", handleStorage);
        }
      };

      const onBroadcast = (event: MessageEvent) => {
        handleMessage(event.data);
      };

      if (this.bc) {
        this.bc.addEventListener("message", onBroadcast);
      } else {
        window.addEventListener("storage", handleStorage);
      }
    });
  }

  private clearResult() {
    localStorage.removeItem(this.RESULT_KEY);
  }
}

// Export singleton
export const refreshService = new RefreshService();
